<template>
  <div
    v-if="data.show"
    class="i-coach-school-display"
  >
    <div class="header">
      <div>The most advanced <br /><strong>AI-Driven</strong> Career Report</div>
      <div class="sub-header">Developed by career experts, trained on millions of data points.</div>
    </div>
    <div class="schools">
      <template
        v-for="school in arrayOfSchools"
        :key="school"
      >
        <img
          class="school"
          loading="lazy"
          :src="school"
        />
      </template>
    </div>
    <div class="coaches">
      <Carousel
        :autoplay="5000"
        :items-to-scroll="1"
        :items-to-show="iemsToShow"
        class="coaches-carousel"
        wrap-around
        snap-align="start"
      >
        <Slide
          v-for="(coach, index) in data.allCoaches"
          :key="index"
        >
          <div class="coach">
            <img
              class="image"
              loading="lazy"
              :src="coach.profilePhotoUrl || ''"
            />
            <div class="bottom">
              <div class="name">{{ coach.fullName }}</div>
              <div class="rating"><img :src="ImageStars" /> {{ coach.rating }} stars</div>
              <div class="highlights">
                <div>HIGHLIGHTS</div>
                <div class="list">
                  <div>
                    <strong>{{ coach.clientsServed }} clients</strong> served
                  </div>
                  <div>
                    <strong>{{ coach.yearsOfExperience || 10 }} years</strong> of experience
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Slide>
        <template #addons>
          <navigation>
            <template #next>
              <span>
                <img
                  :src="iconArrowRightGray"
                  alt="next-icon"
                />
              </span>
            </template>
            <template #prev>
              <span>
                <img
                  :src="iconArrowLeftGray"
                  alt="prev-icon"
                />
              </span>
            </template>
          </navigation>
          <Pagination />
        </template>
      </Carousel>
    </div>
    <div class="action">
      <div class="row-list">
        <div class="detail">Increase Income</div>
        <div class="detail">Job Satisfaction</div>
        <div class="detail">Career Advancement</div>
        <div class="detail">Purposeful Work</div>
        <div class="detail">Flexible Offering</div>
        <div class="detail">Work Life Balance</div>
      </div>
      <iBaseButton
        class="button"
        v-bind="buttonStyle"
        text="Get My Full Career Report"
        @clicked="$emit('scroll-to-payment')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive } from "vue"
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointLg, breakpointMd, breakpointSm, breakpointXl } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import ImageStars from "@/assets/icons/icon-five-slashed-stars.svg"
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import imageSchool6 from "@/assets/welcome/school-duke.svg"
import imageSchool4 from "@/assets/welcome/school-harvard.svg"
import imageSchool2 from "@/assets/welcome/school-north.svg"
import imageSchool1 from "@/assets/welcome/school-chicago.svg"
import imageSchool3 from "@/assets/welcome/school-stanford.svg"
import imageSchool5 from "@/assets/welcome/school-ucla.svg"

// TYPES
import { CoachForSelection } from "@/shared/types/Coach"

// EMITS
defineEmits(["scroll-to-payment"])

//CONSTANTS
const arrayOfSchools = [imageSchool1, imageSchool2, imageSchool3, imageSchool4, imageSchool5, imageSchool6]

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)
const isMdOrLarger = useIsScreenSize(breakpointMd)
const isLOrLarger = useIsScreenSize(breakpointLg)
const isXLorLarger = useIsScreenSize(breakpointXl)

// DATA
const data = reactive({
  allCoaches: [] as CoachForSelection[],
  show: false
})

//COMPUTED
const iemsToShow = computed(() => {
  return isXLorLarger.value ? 5 : isLOrLarger.value ? 3.5 : isMdOrLarger.value ? 2.5 : isSmOrLarger.value ? 2 : 1.5
})
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isSmOrLarger.value ? "max-content" : "100%",
    padding: isSmOrLarger.value ? "8px 70px" : "10px 6px",
    size: "medium"
  }
})

// FUNCTIONS
const fetchCoachesForDisplay = async () => {
  try {
    const response = await api.coach.getListForDisplay()
    data.allCoaches = response.coaches
    data.show = true
  } catch {
    console.error("Error fetching coaches for display")
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchCoachesForDisplay()
})
</script>

<style scoped lang="scss">
.i-coach-school-display {
  background: linear-gradient(180deg, #fff 0%, #eae7ff 100%);
  padding: 50px 15px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 0;
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;
    margin: auto auto 25px;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    strong {
      background-image: $blue-purple-gradient;
      color: transparent;
      background-clip: text;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;
    }
  }

  .coaches-carousel {
    width: 100%;
    margin-bottom: 40px;

    .coach {
      @include flex($direction: column, $justify: start, $align: stretch, $gap: 5px);

      border-radius: 16px;
      background-color: $white;
      box-shadow: 0 10px 25px 1px rgb(0 0 0 / 5%);
      width: 100%;
      height: 100%;
      margin: 0 8px;

      .image {
        width: 100%;
        border-radius: 16px;
        object-position: center;
      }

      .bottom {
        @include flex(column, start, stretch);

        padding: 15px;
        text-align: left;

        .name {
          @include text-lg;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-xl;
          }
        }

        .rating {
          @include text-xs;

          font-weight: 500;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-sm;
          }

          > img {
            vertical-align: sub;
            width: 90px;

            @media screen and (min-width: $breakpoint-md) {
              width: unset;
            }
          }
        }

        .highlights {
          @include flex(column, start, start, $gap: 3px);
          @include text-xs;

          margin-top: 10px;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-sm;
          }

          .list {
            @include flex(column, start, stretch);

            width: 100%;

            > div {
              padding: 5px 0;
              width: 100%;

              &:not(:last-child) {
                border-bottom: 1px solid $zumthor100;
              }
            }
          }
        }
      }
    }
  }

  .schools {
    @include flex(row, center, center, wrap, $gap: 2px 0);

    margin-bottom: 35px;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, center, center, wrap);
    }

    .school {
      width: 90px;

      @media screen and (min-width: $breakpoint-sm) {
        width: 120px;
      }
    }
  }

  .action {
    @include flex(column, center, center, $gap: 35px);

    .row-list {
      @include flex(row, center, center, wrap, $gap: 6px 20px);
      @include text-sm;

      font-weight: 400;

      .detail {
        @include flex(row, start, center);

        color: $black950;
        list-style: none;

        &::before {
          content: "";
          display: inline-block;
          height: 20px;
          width: 25px;
          background-image: url("@/assets/icons/icon-green-check.svg");
          background-repeat: no-repeat;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.i-coach-school-display {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    margin: 0 12%;
    top: calc(100% - 8px);

    @media screen and (min-width: $breakpoint-sm) {
      margin: 0 25%;
    }

    @media screen and (min-width: $breakpoint-md) {
      margin: 0 35%;
    }

    @media screen and (min-width: $breakpoint-lg) {
      margin: 0 38%;
    }

    @media screen and (min-width: $breakpoint-xl) {
      margin: 0 40%;
    }
  }

  .carousel__pagination-button {
    --vc-pgn-width: 5px;
    --vc-pgn-height: 5px;
    --vc-pgn-border-radius: 100%;
    @include border-radius-rounded;
  }

  .carousel__slide {
    margin-bottom: 25px;
  }
  /* stylelint-enable */
}
</style>
